// ============================================
// FILE: components/BulkImportModal.jsx
//
// Changes vs old version:
//  - API_URL reads from import.meta.env.VITE_API_URL (no more hardcoded URLs)
//  - Removed onImportSuccess() call — socket 'bulk:import:complete' event
//    in useLeads already invalidates the query automatically
//  - Everything else (chunking, validation, progress) unchanged
// ============================================

import React, { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  Modal, Box, Text, Button, Group, Stack, Table, Badge,
  Progress, Alert, ScrollArea, ThemeIcon, Paper,
} from '@mantine/core';
import {
  IconFileSpreadsheet, IconCheck, IconAlertCircle,
  IconDownload, IconCloudUpload, IconUpload,
} from '@tabler/icons-react';

const API_URL = 'https://demo-api-384317551782.europe-west1.run.app';

// ─── Column map: Excel header → API field ──────────────────────────────────────
const COLUMN_MAP = {
  'Full Name':             'full_name',
  'Mobile Number':         'mobile_number',
  'Email Address':         'email_address',
  'Configuration':         'configuration',
  'Property Name':         'propertyName',
  'Property Type':         'propertyType',
  'Address':               'address',
  'City':                  'city',
  'Requirement Type':      'requirementType',
  'Service Type':          'serviceType',
  'Lead Acquisition Date': 'leadAcquisitionDate',
  'Meeting Date':          'meeting_date',
  'Meeting Type':          'meeting_type',
  'Presales Name':         'presales_name',
  'Created By':            'createdBy',
  'Sales Rep Id':          'sales_rep_id',
  'Sales Rep':             'sales_rep',
  'Source':                'source',
  'Channel':               'channel',
  'Medium':                'medium',
  'Campaign':              'campaign',
  'Possession':            'possession',
  'Budget':                'budget',
  'Branch':                'branch',
  'Tag':                   'latestTag',
};

const MAX_LEADS_PER_FILE = 500;

// ─── Download template ──────────────────────────────────────────────────────────
const downloadTemplate = () => {
  const headers   = Object.keys(COLUMN_MAP);
  const sampleRow = [
    'John Doe', '9876543210', 'john@example.com', '2BHK', 'Green Valley',
    'Apartment', '123 Main Street', 'Mumbai', 'Buy', 'Resale',
    '2024-01-15', '2024-01-20', 'In Person', 'Alice', 'uid_alice123',
    'uid_bob456', 'Bob Sales', 'Website', 'Online', 'Google',
    'Summer2024', '2025-06-30', '75 Lakhs', 'BLR-KN', '',
  ];
  const ws = XLSX.utils.aoa_to_sheet([headers, sampleRow]);
  ws['!cols'] = headers.map(() => ({ wch: 22 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Leads');
  XLSX.writeFile(wb, 'leads_import_template.xlsx');
};

// ─── Row-level validation ───────────────────────────────────────────────────────
const validateRow = (row) => {
  const errors = [];
  if (!row['Full Name'] || String(row['Full Name']).trim() === '')
    errors.push('"Full Name" is required');
  if (!row['Mobile Number'] || String(row['Mobile Number']).trim() === '')
    errors.push('"Mobile Number" is required');
  else if (!/^\d{10}$/.test(String(row['Mobile Number']).trim()))
    errors.push('"Mobile Number" must be 10 digits');
  return errors;
};

// ─── Generate ALL FABA IDs in ONE single transaction ───────────────────────────
const generateProjectIdsBulk = async (count) => {
  const response = await fetch(`https://api-4h4srbodqa-uc.a.run.app/api/generate-project-ids-bulk`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ count }),
  });
  if (!response.ok) throw new Error('Failed to generate project IDs');
  const result = await response.json();
  if (!result.success) throw new Error(result.message || 'Failed to generate project IDs');
  return result.projectIds;
};

// ─── Firebase upload helpers ────────────────────────────────────────────────────
const uploadToFirebase = async (file, folderName, projectId, index = null) => {
  if (!file) return null;
  const storage    = getStorage();
  const ext        = file.name.substring(file.name.lastIndexOf('.'));
  const storageRef = index !== null
    ? ref(storage, `${projectId}/${folderName}/${folderName}_${index}${ext}`)
    : ref(storage, `${projectId}/${folderName}/${folderName}${ext}`);
  const snapshot = await uploadBytes(storageRef, file);
  return getDownloadURL(snapshot.ref);
};

// ─── Send one chunk of leads to the API ────────────────────────────────────────
const postChunk = async (leads) => {
  const response = await fetch(`${API_URL}/api/leads/bulk-import`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ leads }),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.message || 'Chunk import failed');
  return result;
};

// ───────────────────────────────────────────────────────────────────────────────
export default function BulkImportModal({ opened, onClose, onImportSuccess }) {
  const fileInputRef = useRef(null);

  const [isDragging,     setIsDragging]     = useState(false);
  const [parsedRows,     setParsedRows]     = useState([]);
  const [fileName,       setFileName]       = useState('');
  const [step,           setStep]           = useState('upload'); // upload | preview | importing | done
  const [importProgress, setImportProgress] = useState(0);
  const [progressLabel,  setProgressLabel]  = useState('');
  const [importResults,  setImportResults]  = useState({ success: 0, failed: 0, errors: [] });

  // ─── Parse Excel / CSV ────────────────────────────────────────────────────────
  const parseFile = (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      alert('Please upload an .xlsx, .xls, or .csv file.');
      return;
    }
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const wb   = XLSX.read(data, { type: 'array', cellDates: true });
      const ws   = wb.Sheets[wb.SheetNames[0]];
      const raw  = XLSX.utils.sheet_to_json(ws, { raw: false, defval: '' });

      if (raw.length > MAX_LEADS_PER_FILE) {
        alert(`File has ${raw.length} rows. Maximum allowed is ${MAX_LEADS_PER_FILE} leads per import.`);
        return;
      }

      const rows = raw.map((row) => {
        const errors = validateRow(row);
        const mapped = {};
        Object.entries(COLUMN_MAP).forEach(([excelKey, apiKey]) => {
          mapped[apiKey] = row[excelKey] !== undefined ? String(row[excelKey]).trim() : '';
        });
        return {
          original: row,
          mapped,
          errors,
          status: errors.length === 0 ? 'valid' : 'invalid',
        };
      });

      setParsedRows(rows);
      setStep('preview');
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileInput = (e) => parseFile(e.target.files[0]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    parseFile(e.dataTransfer.files[0]);
  }, []);

  const handleDragOver  = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = ()  => setIsDragging(false);

  // ─── Import ───────────────────────────────────────────────────────────────────
  const handleImport = async () => {
    const validRows = parsedRows
      .filter((r) => r.status === 'valid')
      .map((r) => ({ ...r.mapped, createdAt: new Date().toISOString() }));

    if (validRows.length === 0) return;

    setStep('importing');
    setImportProgress(0);
    setProgressLabel(`Generating ${validRows.length} project IDs...`);

    const results = { success: 0, failed: 0, errors: [] };

    try {
      // Step 1: Reserve all FABA IDs atomically in one transaction
      const projectIds = await generateProjectIdsBulk(validRows.length);

      setImportProgress(10);
      setProgressLabel('Processing leads...');

      // Step 2: Attach projectId + upload any floor plan files
      const leadsWithIds = [];

      for (let i = 0; i < validRows.length; i++) {
        const lead      = { ...validRows[i] };
        const projectId = projectIds[i];

        lead.projectId = projectId;

        let floorPlanUrls = [];
        if (lead._floorPlanFiles?.length > 0) {
          setProgressLabel(`Uploading files for ${projectId} (${i + 1}/${validRows.length})...`);
          for (let j = 0; j < lead._floorPlanFiles.length; j++) {
            const url = await uploadToFirebase(lead._floorPlanFiles[j], 'site-images', projectId, j);
            floorPlanUrls.push(url);
          }
        }

        delete lead._floorPlanFiles;
        leadsWithIds.push({ ...lead, floorPlan: floorPlanUrls });

        // Progress: 10% → 30%
        setImportProgress(10 + Math.round(((i + 1) / validRows.length) * 20));
      }

      // Step 3: Chunk into 400 and POST each chunk
      const CHUNK_SIZE  = 400;
      const totalChunks = Math.ceil(leadsWithIds.length / CHUNK_SIZE);

      for (let c = 0; c < totalChunks; c++) {
        const chunk = leadsWithIds.slice(c * CHUNK_SIZE, (c + 1) * CHUNK_SIZE);

        setProgressLabel(
          totalChunks > 1
            ? `Saving batch ${c + 1} of ${totalChunks} to database...`
            : `Saving ${leadsWithIds.length} leads to database...`
        );

        const result = await postChunk(chunk);

        results.success += result.data?.imported || 0;
        results.failed  += result.data?.failed   || 0;
        result.data?.errors?.forEach((e) => results.errors.push(e));

        // Progress: 30% → 100%
        setImportProgress(30 + Math.round(((c + 1) / totalChunks) * 70));
      }

      setProgressLabel('Done!');

    } catch (err) {
      results.failed = validRows.length;
      results.errors = [{ row: 'All', message: err.message }];
      setImportProgress(100);
    }

    setImportResults(results);
    setStep('done');

    // NOTE: No need to call onImportSuccess() for list refetch —
    // the server emits 'bulk:import:complete' via socket which
    // automatically invalidates the query in useLeads.
    // We only call it if the parent needs to do something else (e.g. close modal).
    if (results.success > 0 && onImportSuccess) {
      onImportSuccess();
    }
  };

  // ─── Reset ────────────────────────────────────────────────────────────────────
  const handleReset = () => {
    setParsedRows([]);
    setFileName('');
    setStep('upload');
    setImportProgress(0);
    setProgressLabel('');
    setImportResults({ success: 0, failed: 0, errors: [] });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const validCount   = parsedRows.filter((r) => r.status === 'valid').length;
  const invalidCount = parsedRows.filter((r) => r.status === 'invalid').length;

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group spacing="xs">
          <ThemeIcon color="blue" variant="light" size="md">
            <IconCloudUpload size={16} />
          </ThemeIcon>
          <Text fw={600} size="lg">Bulk Import Leads</Text>
        </Group>
      }
      size="80%"
      centered
      styles={{ body: { padding: '0 24px 24px' } }}
    >

      {/* ── STEP 1: Upload ──────────────────────────────────────────────────── */}
      {step === 'upload' && (
        <Stack spacing="lg" mt="md">
          <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light">
            <Group position="apart">
              <Text size="sm">
                Download the template, fill in your leads, and upload below.
                Required: <b>Full Name</b>, <b>Mobile Number</b>. Max <b>{MAX_LEADS_PER_FILE} leads</b> per file.
              </Text>
              <Button size="xs" variant="outline" leftIcon={<IconDownload size={14} />} onClick={downloadTemplate}>
                Download Template
              </Button>
            </Group>
          </Alert>

          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${isDragging ? '#228be6' : '#ced4da'}`,
              borderRadius: 12,
              padding: '56px 24px',
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragging ? '#e7f5ff' : '#f8f9fa',
              transition: 'all 0.2s',
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
              onChange={handleFileInput}
            />
            <ThemeIcon size={56} radius="xl" color="blue" variant="light" mx="auto" mb="sm">
              <IconFileSpreadsheet size={28} />
            </ThemeIcon>
            <Text fw={600} size="md" color="dark">
              {isDragging ? 'Drop your file here' : 'Drag & drop your Excel / CSV file'}
            </Text>
            <Text size="sm" color="dimmed" mt={4}>
              or click to browse — .xlsx, .xls, .csv — up to {MAX_LEADS_PER_FILE} leads
            </Text>
          </Box>
        </Stack>
      )}

      {/* ── STEP 2: Preview ─────────────────────────────────────────────────── */}
      {step === 'preview' && (
        <Stack spacing="md" mt="md">
          <Group position="apart">
            <Group spacing="xs">
              <IconFileSpreadsheet size={18} color="#228be6" />
              <Text size="sm" fw={500}>{fileName}</Text>
              <Text size="sm" color="dimmed">({parsedRows.length} rows)</Text>
            </Group>
            <Group spacing="xs">
              <Badge color="teal" variant="light">{validCount} valid</Badge>
              {invalidCount > 0 && (
                <Badge color="red" variant="light">{invalidCount} invalid</Badge>
              )}
              <Button size="xs" variant="subtle" onClick={handleReset}>Change file</Button>
            </Group>
          </Group>

          {invalidCount > 0 && (
            <Alert icon={<IconAlertCircle size={16} />} color="orange" variant="light">
              <Text size="sm">
                {invalidCount} row(s) have errors and will be <b>skipped</b>.
                Fix your file and re-upload, or proceed with valid rows only.
              </Text>
            </Alert>
          )}

          <ScrollArea h={340}>
            <Table striped highlightOnHover withBorder withColumnBorders fontSize="xs">
              <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f1f3f5', zIndex: 1 }}>
                <tr>
                  <th>#</th>
                  <th>Status</th>
                  <th>Full Name</th>
                  <th>Mobile</th>
                  <th>Email</th>
                  <th>City</th>
                  <th>Config</th>
                  <th>Source</th>
                  <th>Presales</th>
                  <th>Sales Rep</th>
                  <th>Budget</th>
                  <th>Errors</th>
                </tr>
              </thead>
              <tbody>
                {parsedRows.map((row, i) => (
                  <tr key={i} style={{ backgroundColor: row.status === 'invalid' ? '#fff5f5' : undefined }}>
                    <td>{i + 1}</td>
                    <td>
                      {row.status === 'valid'
                        ? <Badge color="teal" size="xs" variant="dot">Valid</Badge>
                        : <Badge color="red"  size="xs" variant="dot">Invalid</Badge>}
                    </td>
                    <td>{row.mapped.full_name     || '—'}</td>
                    <td>{row.mapped.mobile_number || '—'}</td>
                    <td>{row.mapped.email_address || '—'}</td>
                    <td>{row.mapped.city          || '—'}</td>
                    <td>{row.mapped.configuration || '—'}</td>
                    <td>{row.mapped.source        || '—'}</td>
                    <td>{row.mapped.presales_name || '—'}</td>
                    <td>{row.mapped.sales_rep     || '—'}</td>
                    <td>{row.mapped.budget        || '—'}</td>
                    <td>
                      {row.errors.length > 0 && (
                        <Text color="red" size="xs">{row.errors.join('; ')}</Text>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </ScrollArea>

          <Group position="right">
            <Button variant="subtle" onClick={handleReset}>Cancel</Button>
            <Button onClick={handleImport} disabled={validCount === 0} leftIcon={<IconCloudUpload size={16} />}>
              Import {validCount} Lead{validCount !== 1 ? 's' : ''}
            </Button>
          </Group>
        </Stack>
      )}

      {/* ── STEP 3: Importing ───────────────────────────────────────────────── */}
      {step === 'importing' && (
        <Stack spacing="lg" mt="lg" align="center" py="xl">
          <ThemeIcon size={64} radius="xl" color="blue" variant="light">
            <IconCloudUpload size={32} />
          </ThemeIcon>
          <Text fw={600} size="lg">Importing leads...</Text>
          <Progress value={importProgress} animate size="lg" w="100%" />
          <Text size="sm" color="dimmed">{progressLabel}</Text>
          <Text size="xs" color="dimmed">Please keep this window open until import completes.</Text>
        </Stack>
      )}

      {/* ── STEP 4: Done ────────────────────────────────────────────────────── */}
      {step === 'done' && (
        <Stack spacing="lg" mt="md">
          <Paper p="lg" radius="md" withBorder style={{ textAlign: 'center' }}>
            <ThemeIcon
              size={64} radius="xl" mx="auto" mb="sm"
              color={importResults.failed === 0 ? 'teal' : 'orange'}
              variant="light"
            >
              {importResults.failed === 0
                ? <IconCheck size={32} />
                : <IconAlertCircle size={32} />}
            </ThemeIcon>
            <Text fw={700} size="lg" mb={4}>
              {importResults.failed === 0 ? 'Import Complete!' : 'Import Finished with Errors'}
            </Text>
            <Group position="center" spacing="xl" mt="md">
              <Stack spacing={2} align="center">
                <Text size="xl" fw={700} color="teal">{importResults.success}</Text>
                <Text size="xs" color="dimmed">Imported</Text>
              </Stack>
              {importResults.failed > 0 && (
                <Stack spacing={2} align="center">
                  <Text size="xl" fw={700} color="red">{importResults.failed}</Text>
                  <Text size="xs" color="dimmed">Failed</Text>
                </Stack>
              )}
            </Group>
          </Paper>

          {importResults.errors.length > 0 && (
            <ScrollArea h={150}>
              <Stack spacing={4}>
                {importResults.errors.map((err, i) => (
                  <Alert key={i} color="red" variant="light" p="xs">
                    <Text size="xs"><b>Row {err.row}:</b> {err.message}</Text>
                  </Alert>
                ))}
              </Stack>
            </ScrollArea>
          )}

          <Group position="right">
            <Button variant="subtle" onClick={handleReset} leftIcon={<IconUpload size={14} />}>
              Import Another File
            </Button>
            <Button onClick={handleClose}>Done</Button>
          </Group>
        </Stack>
      )}

    </Modal>
  );
}